import React, { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import LinkIcon from '@mui/icons-material/Link';
import Slide from '@mui/material/Slide';
import { Stack, Stepper, Step, StepLabel, Box } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { SubjectItem } from 'types/api/SystemTenant/AROKMS/SubjectTypes';
import { ChannelList } from './ChannelList';
import { EventDataChannel } from 'types/api/Tenant/EventDataSourceTypes';
import { PairingColumns } from './PairingColumns';
import { useGetEventDataSourceConfigDetails } from 'services/v1/Tenant/AROEvent/EventDataSourceService';

export type ModalMappingEventDataSourceProps = {
  onClose?: () => void;
  isEdit: boolean;
  eventDataSourceConfigId?: string | null;
  open: boolean;
  eventSubject?: SubjectItem;
};

export enum DATA_CHANNEL_INTEGRATION_STEP {
  SELECT_CHANNEL,
  MAPPING,
  PREVIEW,
}

const STEPS = [
  { label: 'Select Channel', description: 'Choose a data source' },
  { label: 'Map Columns', description: 'Pair source & target fields' },
];

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer', fontSize: 28 };

const stepperStyles = {
  '& .MuiStepIcon-root': {
    fontSize: 28,
    color: '#E3EBF6',
    '&.Mui-active': { color: '#42BB93' },
    '&.Mui-completed': { color: '#42BB93' },
  },
  '& .MuiStepConnector-line': {
    borderColor: '#E3EBF6',
    borderTopWidth: 2,
  },
  '& .Mui-active .MuiStepConnector-line, & .Mui-completed .MuiStepConnector-line': {
    borderColor: '#42BB93',
  },
  '& .MuiStepLabel-label': {
    fontSize: 13,
    fontWeight: 600,
    color: '#828C99',
    '&.Mui-active': { color: '#3B4797', fontWeight: 700 },
    '&.Mui-completed': { color: '#42BB93' },
  },
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

export function ModalConnectDataChannel(props: ModalMappingEventDataSourceProps) {
  const { onClose, open = false, eventSubject, isEdit } = props;

  const [currentStep, setCurrentStep] = React.useState<DATA_CHANNEL_INTEGRATION_STEP>(
    DATA_CHANNEL_INTEGRATION_STEP.SELECT_CHANNEL
  );
  const [selectedChannel, setSelectedChannel] = React.useState<EventDataChannel | null>(null);
  const { data: eventDataSourceConfigDetails, remove } = useGetEventDataSourceConfigDetails(
    eventSubject?.id?.toString()
  );

  const handleOnCompleteSelectChannel = (selectedChannel: EventDataChannel) => {
    setSelectedChannel(selectedChannel);
    setCurrentStep(DATA_CHANNEL_INTEGRATION_STEP.MAPPING);
  };

  const handleOnClose = () => {
    onClose?.();
    setCurrentStep(DATA_CHANNEL_INTEGRATION_STEP.SELECT_CHANNEL);
    remove();
  };

  const handleOnBackToChannelList = () => {
    setCurrentStep(DATA_CHANNEL_INTEGRATION_STEP.SELECT_CHANNEL);
    setSelectedChannel(null);
  };

  useEffect(() => {
    if (eventDataSourceConfigDetails?.data && open) {
      setCurrentStep(DATA_CHANNEL_INTEGRATION_STEP.MAPPING);
      setSelectedChannel(eventDataSourceConfigDetails?.data?.dataChannel);
    }
  }, [eventDataSourceConfigDetails?.data, open]);

  const activeStepIndex = currentStep === DATA_CHANNEL_INTEGRATION_STEP.SELECT_CHANNEL ? 0 : 1;

  return (
    <Dialog fullScreen open={open} onClose={handleOnClose} TransitionComponent={Transition}>
      {/* Header */}
      <Stack
        px={3}
        py={1.5}
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ bgcolor: '#FAFBFD', borderBottom: '1px solid #E3EBF6' }}
      >
        <Stack direction='row' gap={1.5} alignItems='center'>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #42BB93 0%, #3B9F7E 100%)',
            }}
          >
            <LinkIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Stack>
            <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797', fontSize: 16 }}>
              {isEdit ? 'Edit Data Channel Connection' : 'Connect a Data Channel'}
            </Typography>
            <Typography variant='body2' sx={{ color: '#828C99', fontSize: 12 }}>
              {eventSubject?.displayName ? `for ${eventSubject.displayName}` : ''}
            </Typography>
          </Stack>
        </Stack>

        {/* Stepper indicator */}
        <Stepper activeStep={activeStepIndex} sx={{ ...stepperStyles, width: 340 }}>
          {STEPS.map((step) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <IconButton onClick={handleOnClose} size='small' sx={{ '&:hover': { bgcolor: '#FEE2E2' } }}>
          <HighlightOffRoundedIcon sx={closeIconStyle} />
        </IconButton>
      </Stack>

      {/* Content */}
      {currentStep === DATA_CHANNEL_INTEGRATION_STEP.SELECT_CHANNEL && (
        <ChannelList eventSubject={eventSubject} onSelectedChannel={handleOnCompleteSelectChannel} />
      )}
      {currentStep === DATA_CHANNEL_INTEGRATION_STEP.MAPPING && selectedChannel && (
        <PairingColumns
          isEdit={isEdit}
          dataChannel={selectedChannel}
          eventSubject={eventSubject}
          onClose={handleOnClose}
          onBack={!isEdit ? handleOnBackToChannelList : undefined}
        />
      )}
    </Dialog>
  );
}
